// Maps URL slugs to sidebar panel names and onboarding section tabs.
// Single source of truth — used by AdminHome, OnboardingSection, EmployeeOnboarding.

// Non-gamify pages (Onboarding Profiles, Generate Link, My Links).
export const ADMIN_PREFIX = '/admin';
export const EMPLOYEE_PREFIX = '/employee';

// Gamify section URLs — only used when navigating into the "Onboarding"
// sidebar item. Super Admin lands on /gamify-admin/*, Employee on
// /gamify-employee/*. HR / Accounting never reach these URLs because they
// don't see the Onboarding section.
export const GAMIFY_ADMIN_PREFIX = '/gamify-admin';
export const GAMIFY_EMPLOYEE_PREFIX = '/gamify-employee';

// ──────────────────────────────────────────────────────────────────────────
// Role groups
// Backend roles (from EAC) — exact spellings (case-sensitive, including the
// "Maketing" typo that exists in the backend). Source of truth is
// ROLE_GROUP_ALIASES in api/ms_token_validator.py plus DEPARTMENT_ALIASES in
// gamify/eac_context.py:
//   HR, super admin, Accounting             → admin flow (onboarding profiles)
//   Employee, employees                     → branch manager flow (generate-link, my-links)
//   Branch Onboarding, Finance, Maketing,
//   Human Resources, IT, Training,
//   Compliance & Licensing                  → player gamify view (department users)
//
// 'HR' (legacy admin) and 'Human Resources' (gamify department) are two
// different roles on purpose — the backend keeps them separate.
// ──────────────────────────────────────────────────────────────────────────

export const ADMIN_ROLES = ['HR', 'Super Admin', 'super admin', 'Accounting'];

// Branch managers — the historical "Employee" role. Backend may emit either
// 'Employee' (legacy) or 'employees' (current dropdown spelling).
export const BRANCH_MANAGER_ROLES = ['Employee', 'employees'];

// Department users who should see the player gamify view (their own dept's
// projects + tasks only — backend enforces the filter).
export const PLAYER_ROLES = [
  'Branch Onboarding',
  'Finance',
  'Maketing',
  'Marketing',
  // Replaced 'Secondary Maketing' / 'Secondary Marketing' — those roles no
  // longer normalize to a gamify department (gamify migration 0018), so the
  // backend returns 403 for them.
  'Human Resources',
  'IT',
  'Training',
  'Compliance & Licensing',
];

export const isAdminRole = (role) => ADMIN_ROLES.includes(role);
export const isBranchManagerRole = (role) => BRANCH_MANAGER_ROLES.includes(role);
export const isPlayerOnlyRole = (role) => PLAYER_ROLES.includes(role);

// Roles that land on the employee URL prefix (/employee/*, /gamify-employee/*):
// branch managers + department users. Admin roles stay on /admin/*.
export const isEmployeeRole = (role) =>
  isBranchManagerRole(role) || isPlayerOnlyRole(role);

// Roles that see the player gamify dashboard (department-filtered): branch
// managers (legacy behaviour) + department users (new). Admin roles see the
// admin gamify view when they navigate to Onboarding.
export const isPlayerView = (role) =>
  isBranchManagerRole(role) || isPlayerOnlyRole(role);

export const rolePrefix = (role) => (isEmployeeRole(role) ? EMPLOYEE_PREFIX : ADMIN_PREFIX);
export const gamifyPrefix = (role) =>
  isEmployeeRole(role) ? GAMIFY_EMPLOYEE_PREFIX : GAMIFY_ADMIN_PREFIX;

// URL slug -> { sidebar: <sidebar item name>, tab?: <onboarding tab name> }
//
// Admin non-gamify (HR / Accounting / Super Admin):
//   /admin/onboarding-profiles  -> sidebar: 'Onboarding Profiles'
//   /admin/generate-link        -> sidebar: 'Generate Link'
//
// Admin gamify (Super Admin only — Onboarding sidebar item):
//   /gamify-admin/dashboard|projects|tasks -> tab: <Dashboard|Projects|Tasks>
//
// Employee non-gamify (Branch Manager):
//   /employee/generate-link     -> sidebar: 'Generate Link'
//   /employee/my-links          -> sidebar: 'My Links'
//
// Employee gamify (Branch Manager — Onboarding sidebar item):
//   /gamify-employee/dashboard|leaderboard|swag-shop|profile|help -> tab: <...>
export const URL_TO_PANEL = {
  // Shared / admin
  'onboarding-profiles': { sidebar: 'Onboarding Profiles' },
  'generate-link':       { sidebar: 'Generate Link' },
  'my-links':            { sidebar: 'My Links' },
  'dashboard':           { sidebar: 'Onboarding', tab: 'Dashboard' },
  'projects':            { sidebar: 'Onboarding', tab: 'Projects' },
  'tasks':               { sidebar: 'Onboarding', tab: 'Tasks' },
  'challenges':          { sidebar: 'Onboarding', tab: 'Challenges' },
  // Admin swag-item management (Super Admin). Distinct from the employee
  // 'swag-shop' below, which is the player-facing redemption storefront.
  'swag':                { sidebar: 'Onboarding', tab: 'Swag' },
  // Hidden — re-enable along with the TABS entry in onboarding/index.js when
  // admin Employee↔Department management is needed.
  // 'members':             { sidebar: 'Onboarding', tab: 'Members' },
  // Employee-only tabs
  'leaderboard':         { sidebar: 'Onboarding', tab: 'Leaderboard' },
  'swag-shop':           { sidebar: 'Onboarding', tab: 'Swag Shop' },
  'profile':             { sidebar: 'Onboarding', tab: 'Profile' },
  'help':                { sidebar: 'Onboarding', tab: 'Help' },
};

// Reverse map: sidebar item name + optional tab -> slug
export const PANEL_TO_URL = {
  'Onboarding Profiles': 'onboarding-profiles',
  'Generate Link':       'generate-link',
  'My Links':            'my-links',
  // 'Onboarding' resolves by tab below
};

export const tabToSlug = (tabName) => {
  const entry = Object.entries(URL_TO_PANEL).find(
    ([, v]) => v.sidebar === 'Onboarding' && v.tab === tabName
  );
  return entry ? entry[0] : 'dashboard';
};

export const slugToPanel = (slug) => URL_TO_PANEL[slug] || null;

// Default URL after login based on role.
// - Department users (IT, Finance, …) go straight to the player gamify
//   dashboard — that's the only screen they have.
// - Branch managers ('Employee' / 'employees') keep their legacy landing on
//   Generate Link.
// - Admin roles (HR, Super Admin, Accounting) land on Onboarding Profiles.
export const defaultPathForRole = (role) => {
  if (isPlayerOnlyRole(role)) return `${GAMIFY_EMPLOYEE_PREFIX}/dashboard`;
  if (isBranchManagerRole(role)) return `${EMPLOYEE_PREFIX}/generate-link`;
  if (isAdminRole(role)) return `${ADMIN_PREFIX}/onboarding-profiles`;
  return null;
};
